import React, { useEffect, useState } from "react";
import { getData } from "../../services/FetchNodeservices";
import { useSelector } from "react-redux";

/* ---------------- HELPERS ---------------- */

const formatDate = (date) => {
  if (!date) return "-";
  const [y, m, d] = date.split("-");
  return `${d}/${m}/${y}`; // DD/MM/YYYY
};

const calculateDuration = (login, logout) => {
  if (!login) return "00:00:00";
  const end = logout ? new Date(logout) : new Date();
  const diff = (end - new Date(login)) / 1000;
  if (diff < 0) return "00:00:00";

  const hh = String(Math.floor(diff / 3600)).padStart(2, "0");
  const mm = String(Math.floor((diff % 3600) / 60)).padStart(2, "0");
  const ss = String(Math.floor(diff % 60)).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};

const sumDurations = (durations) => {
  let totalSeconds = 0;
  durations.forEach((d) => {
    if (!d) return;
    const [hh, mm, ss] = d.split(":");
    totalSeconds += hh * 3600 + mm * 60 + ss * 1;
  });

  const hh = String(Math.floor(totalSeconds / 3600)).padStart(2, "0");
  const mm = String(Math.floor((totalSeconds % 3600) / 60)).padStart(2, "0");
  const ss = String(totalSeconds % 60).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
};

/* ---------------- COMPONENT ---------------- */

export default function LoginLogoutDashboard() {
  const user = useSelector((state) => state.user);

  const [rows, setRows] = useState([]);
  const [filtered, setFiltered] = useState([]);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  /* -------- LIVE TICKER -------- */
  useEffect(() => {
    const interval = setInterval(() => {
      setFiltered((prev) => [...prev]);
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  /* -------- FETCH ATTENDANCE -------- */
  useEffect(() => {
    const fetchData = async () => {
      if (!user?.id) return;

      const resp = await getData(`attendances/user/${user.id}`);
      if (!resp?.status) return;

      const data = resp.data;

      // ✅ FIX: derive date from punch_in_time (LOCAL TIME)
      const flattened = Object.values(data).flatMap((rows) =>
        rows.map((r) => {
          const localDate = new Date(r.punch_in_time)
            .toLocaleDateString("en-CA"); // YYYY-MM-DD (local)

          return {
            date: localDate,
            punchIn: r.punch_in_time,
            punchOut: r.punch_out_time,
            empCode: r.employee_code,
            userId: r.user_id,
            name: `${user.first_name} ${user.last_name}`,
          };
        })
      );

      flattened.sort(
        (a, b) => new Date(b.punchIn) - new Date(a.punchIn)
      );

      setRows(flattened);
      setFiltered(flattened);
    };

    fetchData();
  }, [user]);

  /* -------- APPLY FILTER -------- */
  const applyFilter = () => {
    let f = [...rows];
    if (startDate) f = f.filter((r) => r.date >= startDate);
    if (endDate) f = f.filter((r) => r.date <= endDate);
    setFiltered(f);
  };

  /* -------- GROUP BY DATE -------- */
  const grouped = filtered.reduce((acc, row) => {
    const key = formatDate(row.date);
    if (!acc[key]) acc[key] = [];
    acc[key].push(row);
    return acc;
  }, {});

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-black text-gray-900 tracking-tight">
          Attendance Log
        </h1>
        <p className="text-sm font-semibold text-gray-400 mt-1">
          Review and filter your login, logout history and active durations
        </p>
      </div>

      {/* Filters Card */}
      <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-2xl shadow-indigo-100/20">
        <div className="flex flex-wrap items-end gap-4">
          <div className="w-full sm:w-auto flex-1 min-w-[200px]">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">Start Date</label>
            <input
              type="date"
              className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-medium"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="w-full sm:w-auto flex-1 min-w-[200px]">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider block mb-2">End Date</label>
            <input
              type="date"
              className="w-full px-4 py-2.5 rounded-2xl bg-[#f4f6fa]/50 text-gray-800 border border-gray-100 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-100/50 transition duration-200 text-sm font-medium"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <button
            onClick={applyFilter}
            className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700 text-white font-bold px-6 py-2.5 rounded-2xl shadow-lg shadow-indigo-100 hover:shadow-indigo-200 transition duration-200 text-sm"
          >
            Apply Filters
          </button>
        </div>
      </div>

      {/* Attendance List */}
      <div className="space-y-6">
        {Object.keys(grouped).length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-3xl p-12 text-center text-gray-400 font-semibold shadow-md">
            No attendance records found matching filters.
          </div>
        ) : (
          Object.keys(grouped).map((date, ix) => {
            const dayRows = grouped[date];
            const totalDayDuration = sumDurations(
              dayRows.map((r) =>
                calculateDuration(r.punchIn, r.punchOut)
              )
            );

            return (
              <div key={ix} className="bg-white border border-gray-100 rounded-3xl shadow-xl shadow-indigo-100/20 overflow-hidden">
                {/* Day Header */}
                <div className="bg-indigo-50/30 border-b border-indigo-50 px-6 py-4 flex flex-col sm:flex-row justify-between sm:items-center gap-2">
                  <h3 className="font-extrabold text-gray-800 text-base">{date}</h3>
                  <span className="text-xs font-black text-indigo-600 bg-indigo-50 border border-indigo-100/50 px-3 py-1.5 rounded-2xl">
                    Total Active Hours: {totalDayDuration}
                  </span>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left border-collapse">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50/50 text-[10px] font-bold text-gray-400 uppercase tracking-wider">
                        <th className="px-6 py-3.5">User ID</th>
                        <th className="px-6 py-3.5">Emp Code</th>
                        <th className="px-6 py-3.5">Name</th>
                        <th className="px-6 py-3.5">Login Time</th>
                        <th className="px-6 py-3.5">Logout Time</th>
                        <th className="px-6 py-3.5">Status</th>
                        <th className="px-6 py-3.5 text-right">Duration</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-50">
                      {dayRows.map((row, idx) => {
                        const duration = calculateDuration(
                          row.punchIn,
                          row.punchOut
                        );
                        return (
                          <tr key={idx} className="hover:bg-gray-50/30 transition text-gray-700">
                            <td className="px-6 py-4 font-semibold text-gray-500">#{row.userId}</td>
                            <td className="px-6 py-4 font-bold text-gray-900">{row.empCode}</td>
                            <td className="px-6 py-4 font-semibold text-gray-800">{row.name}</td>
                            <td className="px-6 py-4 font-medium text-gray-600">
                              {row.punchIn
                                ? new Date(row.punchIn).toLocaleTimeString("en-GB")
                                : "-"}
                            </td>
                            <td className="px-6 py-4 font-medium text-gray-600">
                              {row.punchOut
                                ? new Date(row.punchOut).toLocaleTimeString("en-GB")
                                : "-"}
                            </td>
                            <td className="px-6 py-4">
                              <span
                                className={`px-2.5 py-1 rounded-full text-[10px] font-extrabold uppercase border tracking-wider inline-block ${
                                  row.punchOut
                                    ? "bg-red-50 text-red-600 border-red-100"
                                    : "bg-green-50 text-green-600 border-green-100"
                                }`}
                              >
                                {row.punchOut ? "Logged Out" : "Active"}
                              </span>
                            </td>
                            <td className="px-6 py-4 font-bold text-gray-900 text-right">{duration}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

